/**
 * Four real TMUA question shapes and one generated Question, written in the schema.
 *
 * Chosen to cover what the bare schema cannot show on its own: options that are not plain
 * answers, a stem carrying an enumerated list, options whose text is a row of a grid, a figure
 * drawn as inline SVG, and a difficulty estimate with a tip.
 *
 * The markup is what `SimpSite` actually consumes, checked against
 * `SimpSite/fixtures/sim-config.specimen.json`: bare `<p>`, `<ol>`, `<li>`, `<b>`, `<math>` and
 * inline `<svg>`, and nothing else. That is how these examples are written rather than a rule
 * they obey — the upload accepts a `class` or a `style`. Reaching for one is still worth a
 * second thought: the `abe-` classes belong to the driver's own wrappers, and five colour
 * schemes swap whole stylesheets under a colour written into the markup. The conventions the
 * markup follows — a symbol always in `<math>`, an `<mrow>` round every bracketed group, a
 * figure's symbol labels marked `serif` — are the authoring conventions, kept in the
 * `ExamFoundationAuthoring` repository.
 *
 * Topic Codes follow the UAT-UK specification where one covers the ground — MM2 Sequences and
 * series, MM5 Exponentials and logarithms, MM6 Differentiation, MM8 Graphs of functions — because
 * that is what the reference list will most likely hold. Nothing here depends on it.
 *
 * The schema itself is served by the API (`/admin/questions/schema`, app:R35) and shown above
 * these. The examples are authored here, beside the screen that pages them, and
 * `QuestionSchemaExamplesTests` in the API's unit tests runs every one through the validator.
 */

/** One worked example: a real question shape, written in the schema. */
export interface SchemaExample {
  title: string
  note: string
  yaml: string
}

export const QUESTION_SCHEMA_EXAMPLES: readonly SchemaExample[] = [
  {
    title: 'A plain multiple choice',
    note: 'The ordinary case. Six options, each one an answer in its own right, each a bare <math> as the Sim fixture writes them. The tip is HTML like the hint: it is advice about how to work rather than part of the wording, but the quantities it names are set as quantities.',
    yaml: `id: 0b6f2c54-3a1d-4c8e-9f27-5d1a8e3b7c10
type: generated
exam_code: TMUA
topic_code: MM2
topic: Sequences and series
difficulty: Medium
ExamOrder: 1
stem: |
  <p>The non-zero constant <math><mi>k</mi></math> is chosen so that the coefficients of <math><msup><mi>x</mi><mn>6</mn></msup></math> in the expansions of <math><msup><mrow><mo>(</mo><mn>1</mn><mo>+</mo><mi>k</mi><msup><mi>x</mi><mn>2</mn></msup><mo>)</mo></mrow><mn>7</mn></msup></math> and <math><msup><mrow><mo>(</mo><mi>k</mi><mo>+</mo><mi>x</mi><mo>)</mo></mrow><mn>10</mn></msup></math> are equal.</p>
  <p>What is the value of <math><mi>k</mi></math>?</p>
hint: |
  <p>Only one term in each expansion matters. Which power of <math><mi>x</mi></math> is it, in each?</p>
tip: |
  <p>Only the two binomial coefficients matter, so <math><mi>k</mi><mo>=</mo><mfrac><mn>35</mn><mn>210</mn></mfrac></math> straight away &#x2014; neither expansion has to be written out. The four surd options can go on sight: a ratio of two integers cannot carry a root.</p>
options:
  A: '<math><mfrac><mn>1</mn><mn>6</mn></mfrac></math>'
  B: '<math><mn>6</mn></math>'
  C: '<math><mfrac><msqrt><mn>6</mn></msqrt><mn>6</mn></mfrac></math>'
  D: '<math><msqrt><mn>6</mn></msqrt></math>'
  E: '<math><mfrac><msqrt><mn>30</mn></msqrt><mn>30</mn></mfrac></math>'
  F: '<math><msqrt><mn>30</mn></msqrt></math>'
correct: A
solution: |
  <p>Expand both and look only at the term in <math><msup><mi>x</mi><mn>6</mn></msup></math>.</p>
  <p>In <math><msup><mrow><mo>(</mo><mn>1</mn><mo>+</mo><mi>k</mi><msup><mi>x</mi><mn>2</mn></msup><mo>)</mo></mrow><mn>7</mn></msup></math> it is the <math><mi>r</mi><mo>=</mo><mn>3</mn></math> term, <math><mn>35</mn><msup><mi>k</mi><mn>3</mn></msup><msup><mi>x</mi><mn>6</mn></msup></math>.</p>
  <p>In <math><msup><mrow><mo>(</mo><mi>k</mi><mo>+</mo><mi>x</mi><mo>)</mo></mrow><mn>10</mn></msup></math> it is the <math><mi>r</mi><mo>=</mo><mn>6</mn></math> term, <math><mn>210</mn><msup><mi>k</mi><mn>4</mn></msup><msup><mi>x</mi><mn>6</mn></msup></math>.</p>
  <p>So <math><mn>35</mn><msup><mi>k</mi><mn>3</mn></msup><mo>=</mo><mn>210</mn><msup><mi>k</mi><mn>4</mn></msup></math>, and <math><mi>k</mi></math> is non-zero, so <math><mi>k</mi><mo>=</mo><mfrac><mn>35</mn><mn>210</mn></mfrac><mo>=</mo><mfrac><mn>1</mn><mn>6</mn></mfrac></math>.</p>
`,
  },
  {
    title: 'Statements I, II and III',
    note: 'The statements belong to the stem, as an <ol>. The Sim leaves a content list its own 40px indent, which is what draws them the way the paper does. No hint: the field is optional and this file leaves it out.',
    yaml: `id: 4e9a7d31-6c2b-4f05-8b1e-2a7c9d0f6e43
type: generated
exam_code: TMUA
topic_code: MM5
topic: Exponentials and logarithms
difficulty: Hard
ExamOrder: 1
stem: |
  <p>Consider the equation <math><msup><mn>2</mn><mi>x</mi></msup><mo>=</mo><mi>m</mi><mi>x</mi><mo>+</mo><mi>c</mi></math>, where <math><mi>m</mi></math> and <math><mi>c</mi></math> are real constants.</p>
  <p>Which of the following statements is/are true?</p>
  <ol type="I">
    <li>The equation has a negative real solution <b>only if</b> <math><mi>c</mi><mo>&gt;</mo><mn>1</mn></math></li>
    <li>The equation has two distinct real solutions <b>if</b> <math><mi>c</mi><mo>&gt;</mo><mn>1</mn></math></li>
    <li>The equation has two distinct positive real solutions <b>if and only if</b> <math><mi>c</mi><mo>&#x2264;</mo><mn>1</mn></math></li>
  </ol>
options:
  A: 'none of them'
  B: 'I only'
  C: 'II only'
  D: 'III only'
  E: 'I and II only'
  F: 'I and III only'
  G: 'II and III only'
  H: 'I, II and III'
correct: A
solution: |
  <p>I fails: <math><mi>m</mi><mo>=</mo><mn>0</mn></math>, <math><mi>c</mi><mo>=</mo><mfrac><mn>1</mn><mn>2</mn></mfrac></math> gives <math><mi>x</mi><mo>=</mo><mo>-</mo><mn>1</mn></math> with <math><mi>c</mi><mo>&#x2264;</mo><mn>1</mn></math>.</p>
  <p>II fails: <math><mi>m</mi><mo>=</mo><mn>0</mn></math>, <math><mi>c</mi><mo>=</mo><mn>2</mn></math> gives one solution, not two.</p>
  <p>III fails the <b>if</b> half: <math><mi>m</mi><mo>=</mo><mn>0</mn></math>, <math><mi>c</mi><mo>=</mo><mn>1</mn></math> gives <math><mi>x</mi><mo>=</mo><mn>0</mn></math>, which is not positive.</p>
`,
  },
  {
    title: 'A table of conditions',
    note: 'The paper draws eight rows of a grid. Each row IS an option, so the row becomes the option text and the grid is only how the paper renders it.',
    yaml: `id: 9c3d5e18-7f4a-4b62-a0d9-6e1b2c8f4a75
type: generated
exam_code: TMUA
topic_code: MM8
topic: Graphs of functions
difficulty: Hard
ExamOrder: 1
stem: |
  <p>Which of these conditions is/are <b>necessary and sufficient</b> for the graph of <math><mi>y</mi><mo>=</mo><mi>f</mi><mrow><mo>(</mo><mi>x</mi><mo>)</mo></mrow></math> to have reflection symmetry in the line <math><mi>x</mi><mo>=</mo><mi>a</mi></math>?</p>
  <ol type="I">
    <li><math><mi>f</mi><mrow><mo>(</mo><mi>a</mi><mo>+</mo><mi>x</mi><mo>)</mo></mrow><mo>=</mo><mi>f</mi><mrow><mo>(</mo><mi>a</mi><mo>-</mo><mi>x</mi><mo>)</mo></mrow></math> for all <math><mi>x</mi></math></li>
    <li><math><mi>f</mi><mrow><mo>(</mo><mi>x</mi><mo>)</mo></mrow><mo>=</mo><mi>f</mi><mrow><mo>(</mo><mn>2</mn><mi>a</mi><mo>-</mo><mi>x</mi><mo>)</mo></mrow></math> for all <math><mi>x</mi></math></li>
    <li><math><msup><mi>f</mi><mo>&#x2032;</mo></msup><mrow><mo>(</mo><mi>a</mi><mo>)</mo></mrow><mo>=</mo><mn>0</mn></math></li>
  </ol>
  <p>Each option says, in order, whether I, II and III is necessary and sufficient.</p>
hint: |
  <p>Write down what reflection symmetry in <math><mi>x</mi><mo>=</mo><mi>a</mi></math> means, then compare each condition to it.</p>
options:
  A: 'yes, yes, yes'
  B: 'yes, yes, no'
  C: 'yes, no, yes'
  D: 'yes, no, no'
  E: 'no, yes, yes'
  F: 'no, yes, no'
  G: 'no, no, yes'
  H: 'no, no, no'
correct: B
solution: |
  <p>I and II say the same thing one substitution apart, and each is exactly the definition of reflection symmetry in <math><mi>x</mi><mo>=</mo><mi>a</mi></math>.</p>
  <p>III is neither necessary nor sufficient: <math><mi>f</mi></math> need not be differentiable at <math><mi>a</mi></math>, and a horizontal tangent is satisfied by any turning point.</p>
`,
  },
  {
    title: 'A question with a diagram',
    note: 'The figure is inline SVG in the stem, drawn in currentColor so the five colour schemes recolour it, and sized in pixels because nothing in the Sim styles a content svg -- a viewBox on its own stretches it to the width of the pane. Its point labels are symbols, so they carry the generic serif face and italic, as the same letters would in <math>. The options are those point names, symbols too, so each is a <math>; that the option text and the option letter coincide is the paper, not the schema.',
    yaml: `id: d27a1f60-8e5c-4d39-b4a2-0f6c3e9b1d58
type: generated
exam_code: TMUA
topic_code: MM6
topic: Differentiation
difficulty: Easy
ExamOrder: 1
stem: |
  <p>The function <math><mi>f</mi><mrow><mo>(</mo><mi>x</mi><mo>)</mo></mrow></math> has derivative <math><msup><mi>f</mi><mo>&#x2032;</mo></msup><mrow><mo>(</mo><mi>x</mi><mo>)</mo></mrow></math>.</p>
  <p>The diagram below shows the graph of <math><mi>y</mi><mo>=</mo><msup><mi>f</mi><mo>&#x2032;</mo></msup><mrow><mo>(</mo><mi>x</mi><mo>)</mo></mrow></math>.</p>
  <svg viewBox="0 0 320 170" width="320" height="170" role="img" aria-label="The graph of the derivative, with six labelled points"><title>The graph of y = f'(x)</title>
  <path d="M10 80 L310 80" stroke="currentColor" fill="none" stroke-width="1"/>
  <path d="M20 25 C 40 45, 50 60, 60 80 C 72 100, 82 135, 95 135 C 108 135, 120 95, 130 80 C 145 55, 165 25, 180 25 C 195 25, 213 80, 225 80 C 237 80, 250 50, 300 45" stroke="currentColor" fill="none" stroke-width="1.5"/>
  <g fill="currentColor"><circle cx="60" cy="80" r="3"/><circle cx="95" cy="135" r="3"/><circle cx="130" cy="80" r="3"/><circle cx="155" cy="43" r="3"/><circle cx="180" cy="25" r="3"/><circle cx="225" cy="80" r="3"/></g>
  <g font-family="serif" font-style="italic" font-size="12" fill="currentColor"><text x="52" y="99">A</text><text x="99" y="150">B</text><text x="124" y="99">C</text><text x="149" y="35">D</text><text x="174" y="17">E</text><text x="219" y="99">F</text></g></svg>
  <p>Which point corresponds to a local minimum of <math><mi>f</mi><mrow><mo>(</mo><mi>x</mi><mo>)</mo></mrow></math>?</p>
hint: |
  <p>The graph shown is of <math><msup><mi>f</mi><mo>&#x2032;</mo></msup></math>, not <math><mi>f</mi></math>. What does a minimum of <math><mi>f</mi></math> look like on it?</p>
options:
  A: '<math><mi>A</mi></math>'
  B: '<math><mi>B</mi></math>'
  C: '<math><mi>C</mi></math>'
  D: '<math><mi>D</mi></math>'
  E: '<math><mi>E</mi></math>'
  F: '<math><mi>F</mi></math>'
correct: C
solution: |
  <p>A local minimum of <math><mi>f</mi></math> needs <math><msup><mi>f</mi><mo>&#x2032;</mo></msup></math> to cross zero from negative to positive. C is the only labelled point where that happens.</p>
`,
  },
  {
    title: 'A generated Question',
    note: 'The wording is original, and difficulty is the generator estimate until the Checker sets the final value. Its id is what a later upload matches. Two Distractors carry Option Feedback, read in Review beside the one a Student chose. Its tip is the case tip exists for: the options settle it faster than the algebra does, and it names the values it substitutes as maths.',
    yaml: `id: 7c1e4b2a-9f0d-4e6b-8a53-2d1f6c0e9b47
type: generated
exam_code: TMUA
topic_code: MM1
topic: Algebra and functions
difficulty: Medium
ExamOrder: 1
stem: |
  <p>The equation <math><msup><mi>x</mi><mn>2</mn></msup><mo>-</mo><mn>2</mn><mi>p</mi><mi>x</mi><mo>+</mo><mi>p</mi><mo>+</mo><mn>6</mn><mo>=</mo><mn>0</mn></math> has two distinct real roots, both positive.</p>
  <p>What is the complete set of possible values of <math><mi>p</mi></math>?</p>
hint: |
  <p>Distinct real roots is one condition. Both positive is two more — what do the sum and the product of the roots tell you?</p>
tip: |
  <p>Faster from the options than from the algebra. <math><mi>p</mi><mo>=</mo><mn>0</mn></math> gives <math><msup><mi>x</mi><mn>2</mn></msup><mo>+</mo><mn>6</mn><mo>=</mo><mn>0</mn></math>, no real roots, which kills three of them; <math><mi>p</mi><mo>=</mo><mo>&#x2212;</mo><mn>3</mn></math> gives two negative roots, which kills a fourth. What is left is the answer, and nothing was solved.</p>
options:
  A: '<math><mi>p</mi><mo>&gt;</mo><mn>3</mn></math>'
  B: '<math><mi>p</mi><mo>&gt;</mo><mo>-</mo><mn>2</mn></math>'
  C: '<math><mi>p</mi><mo>&lt;</mo><mo>-</mo><mn>2</mn></math> or <math><mi>p</mi><mo>&gt;</mo><mn>3</mn></math>'
  D: '<math><mo>-</mo><mn>2</mn><mo>&lt;</mo><mi>p</mi><mo>&lt;</mo><mn>3</mn></math>'
  E: '<math><mi>p</mi><mo>&gt;</mo><mo>-</mo><mn>6</mn></math>'
correct: A
option_feedback:
  C: |
    <p>This answer can come from stopping at the discriminant. Distinct real roots allow <math><mi>p</mi><mo>&lt;</mo><mo>-</mo><mn>2</mn></math>, so test the sign of the roots there: their sum <math><mn>2</mn><mi>p</mi></math> is negative.</p>
  D: |
    <p>This answer can come from reversing the inequality. <math><mrow><mo>(</mo><mi>p</mi><mo>-</mo><mn>3</mn><mo>)</mo></mrow><mrow><mo>(</mo><mi>p</mi><mo>+</mo><mn>2</mn><mo>)</mo></mrow><mo>&gt;</mo><mn>0</mn></math> holds outside the two values, not between them.</p>
solution: |
  <p>Two distinct real roots need <math><msup><mrow><mo>(</mo><mo>-</mo><mn>2</mn><mi>p</mi><mo>)</mo></mrow><mn>2</mn></msup><mo>-</mo><mn>4</mn><mrow><mo>(</mo><mi>p</mi><mo>+</mo><mn>6</mn><mo>)</mo></mrow><mo>&gt;</mo><mn>0</mn></math>, that is <math><mrow><mo>(</mo><mi>p</mi><mo>-</mo><mn>3</mn><mo>)</mo></mrow><mrow><mo>(</mo><mi>p</mi><mo>+</mo><mn>2</mn><mo>)</mo></mrow><mo>&gt;</mo><mn>0</mn></math>, so <math><mi>p</mi><mo>&lt;</mo><mo>-</mo><mn>2</mn></math> or <math><mi>p</mi><mo>&gt;</mo><mn>3</mn></math>.</p>
  <p>Both roots positive needs the sum <math><mn>2</mn><mi>p</mi><mo>&gt;</mo><mn>0</mn></math> and the product <math><mi>p</mi><mo>+</mo><mn>6</mn><mo>&gt;</mo><mn>0</mn></math>, so <math><mi>p</mi><mo>&gt;</mo><mn>0</mn></math>.</p>
  <p>Together, <math><mi>p</mi><mo>&gt;</mo><mn>3</mn></math>.</p>
`,
  },
]
